import { useState } from 'react';

const SCALES = [
  { value: '1/50', factor: 50, label: '1:50', desc: 'Détaillé' },
  { value: '1/75', factor: 75, label: '1:75', desc: 'Intermédiaire' },
  { value: '1/100', factor: 100, label: '1:100', desc: 'Standard' },
  { value: '1/200', factor: 200, label: '1:200', desc: 'Vue globale' },
];

const RulerWidget = ({ value = '1/100', onChange }) => {
  const [hoveredScale, setHoveredScale] = useState(null);
  const selected = SCALES.find(s => s.value === value) || SCALES[2];
  const width = 320;
  const rulerHeight = 48;
  const topPad = 8;

  // Generate ruler marks based on selected scale
  const generateMarks = (scale) => {
    const marks = [];
    const cmPerUnit = 100 / scale.factor; // cm on paper for 1m real
    const pxPerCm = width / 16; // We show 16cm of ruler
    const totalCm = 16;

    for (let i = 0; i <= totalCm; i++) {
      const x = i * pxPerCm;
      const isMajor = i % 5 === 0;
      const isMid = i % 2.5 === 0 && !isMajor;
      const height = isMajor ? 20 : isMid ? 14 : 8;
      marks.push({ x, height, isMajor, label: isMajor ? `${i}` : null });
    }
    return { marks, pxPerCm };
  };

  const { marks, pxPerCm } = generateMarks(selected);

  // The "real meters" span shown on ruler
  const realMeters = Math.round((16 / (100 / selected.factor)) * 10) / 10;

  return (
    <div className="flex flex-col gap-3" data-testid="ruler-widget">
      {/* Scale selector pills */}
      <div className="flex gap-1.5">
        {SCALES.map((scale) => {
          const isActive = scale.value === value;
          const isHovered = hoveredScale === scale.value;
          return (
            <button
              key={scale.value}
              onClick={() => onChange(scale.value)}
              onMouseEnter={() => setHoveredScale(scale.value)}
              onMouseLeave={() => setHoveredScale(null)}
              data-testid={`ruler-scale-${scale.value.replace('/', '-')}`}
              className={`
                flex-1 relative rounded-lg px-2 py-2 text-center transition-all duration-200 border-2
                ${isActive
                  ? 'bg-slate-900 border-blue-500 text-white shadow-lg shadow-blue-500/20'
                  : isHovered
                    ? 'bg-slate-100 border-slate-300 text-slate-800'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                }
              `}
            >
              <div className={`text-sm font-bold font-mono ${isActive ? 'text-blue-400' : ''}`}>
                {scale.label}
              </div>
              <div className={`text-[9px] mt-0.5 ${isActive ? 'text-slate-400' : 'text-slate-400'}`}>
                {scale.desc}
              </div>
              {isActive && (
                <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-2 h-2 bg-blue-500 rotate-45 rounded-[1px]" />
              )}
            </button>
          );
        })}
      </div>

      {/* Visual ruler */}
      <div className="relative bg-slate-900 rounded-xl p-3 border border-slate-700 overflow-hidden">
        {/* Grid pattern background */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)`,
            backgroundSize: '20px 20px'
          }}
        />

        <svg width={width} height={rulerHeight + topPad + 24} viewBox={`0 0 ${width} ${rulerHeight + topPad + 24}`} className="relative">
          {/* Ruler body */}
          <rect x="0" y={topPad} width={width} height={rulerHeight} rx="4" fill="#1e293b" stroke="#334155" strokeWidth="1" />
          
          {/* Graduated yellow strip */}
          <rect x="0" y={topPad} width={width} height="6" rx="2" fill="#eab308" opacity="0.8" />

          {/* Tick marks */}
          {marks.map((mark, i) => (
            <g key={i}>
              <line
                x1={mark.x}
                y1={topPad + 6}
                x2={mark.x}
                y2={topPad + 6 + mark.height}
                stroke={mark.isMajor ? '#94a3b8' : '#475569'}
                strokeWidth={mark.isMajor ? 1.5 : 0.8}
              />
              {mark.label && (
                <text
                  x={mark.x}
                  y={topPad + 6 + mark.height + 12}
                  textAnchor="middle"
                  fill="#94a3b8"
                  fontSize="9"
                  fontFamily="'Outfit', monospace"
                >
                  {mark.label}
                </text>
              )}
            </g>
          ))}

          {/* Real-world measurement annotation */}
          {(() => {
            const annotStart = 0;
            const annotEnd = Math.min(5 * pxPerCm, width - 10);
            const meterLabel = `${Math.round(5 / (100 / selected.factor) * 10) / 10}m réel`;
            const y = topPad + rulerHeight + 8;
            return (
              <g>
                <line x1={annotStart + 2} y1={y} x2={annotEnd - 2} y2={y} stroke="#3b82f6" strokeWidth="1.5" />
                <line x1={annotStart + 2} y1={y - 3} x2={annotStart + 2} y2={y + 3} stroke="#3b82f6" strokeWidth="1.5" />
                <line x1={annotEnd - 2} y1={y - 3} x2={annotEnd - 2} y2={y + 3} stroke="#3b82f6" strokeWidth="1.5" />
                {/* Arrow heads */}
                <polygon points={`${annotStart + 2},${y} ${annotStart + 7},${y - 2.5} ${annotStart + 7},${y + 2.5}`} fill="#3b82f6" />
                <polygon points={`${annotEnd - 2},${y} ${annotEnd - 7},${y - 2.5} ${annotEnd - 7},${y + 2.5}`} fill="#3b82f6" />
                <text x={(annotStart + annotEnd) / 2} y={y + 12} textAnchor="middle" fill="#60a5fa" fontSize="9" fontFamily="'Outfit', sans-serif" fontWeight="600">
                  {meterLabel}
                </text>
              </g>
            );
          })()}

          {/* cm label on right */}
          <text x={width - 4} y={topPad + rulerHeight / 2 + 4} textAnchor="end" fill="#64748b" fontSize="8" fontFamily="'Outfit', sans-serif">
            cm
          </text>
        </svg>

        {/* Scale info bar */}
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-700/50">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-500 uppercase tracking-wider">Échelle</span>
            <span className="text-sm font-mono font-bold text-blue-400" data-testid="ruler-value">
              {selected.label}
            </span>
          </div>
          <div className="text-[10px] text-slate-500">
            1cm plan = {selected.factor / 100}m réel | ~{realMeters}m sur 16cm
          </div>
        </div>
      </div>
    </div>
  );
};

export { RulerWidget };
